import { useEffect, useMemo, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { antigenApi } from "api/immunization";
import ExternalCodesField from "./common/ExternalCodesField";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import {
  validateCode,
  validateExternalCodes,
  validateRequired,
} from "./common/validation";

const AntigenModal = ({ open, onClose, data }) => {
  const antigenId = data?._id;
  const isUpdate = Boolean(antigenId);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      antigenName: data?.antigenName || "",
      diseaseName: data?.diseaseName || "",
      diseaseGroup: data?.diseaseGroup || "",
      patientDescription: data?.patientDescription || "",
      isNotifiable: Boolean(data?.isNotifiable),
      confersLifetimeImmunity: Boolean(data?.confersLifetimeImmunity),
      defaultBoosterIntervalDays: data?.defaultBoosterIntervalDays ?? "",
      externalCodes:
        Array.isArray(data?.externalCodes) ? data.externalCodes : [],
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const validationError = useMemo(
    () =>
      validateCode(form.code, { isUpdate, label: "Antigen code" }) ||
      validateRequired(form.antigenName, "Antigen name") ||
      (form.confersLifetimeImmunity && form.defaultBoosterIntervalDays !== "" ?
        "An antigen conferring lifetime immunity cannot define a booster interval"
      : "") ||
      validateExternalCodes(form.externalCodes),
    [form, isUpdate],
  );

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      antigenName: form.antigenName.trim(),
      diseaseName: form.diseaseName?.trim() || "",
      diseaseGroup: form.diseaseGroup?.trim() || "",
      patientDescription: form.patientDescription?.trim() || "",
      isNotifiable: form.isNotifiable,
      confersLifetimeImmunity: form.confersLifetimeImmunity,
      defaultBoosterIntervalDays:
        form.defaultBoosterIntervalDays === "" ?
          null
        : Number(form.defaultBoosterIntervalDays),
      externalCodes: form.externalCodes,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await antigenApi.update(antigenId, payload);
        toast.success("Antigen updated successfully");
      } else {
        await antigenApi.create({ ...payload, code: form.code.trim() });
        toast.success("Antigen created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} antigen`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Antigen"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
    >
      <CodeField
        value={form.code}
        onChange={(value) => set({ code: value })}
        isUpdate={isUpdate}
        label="Antigen Code"
        helperText='e.g. "hepatitis b" becomes HEPATITIS_B'
      />

      <TextField
        label="Antigen Name"
        required
        fullWidth
        size="small"
        value={form.antigenName || ""}
        onChange={(event) => set({ antigenName: event.target.value })}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          label="Disease Name"
          fullWidth
          size="small"
          value={form.diseaseName || ""}
          onChange={(event) => set({ diseaseName: event.target.value })}
        />
        <TextField
          label="Disease Group"
          fullWidth
          size="small"
          value={form.diseaseGroup || ""}
          onChange={(event) => set({ diseaseGroup: event.target.value })}
          helperText="Free text, for catalogue grouping"
        />
      </Box>

      <TextField
        label="Patient Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.patientDescription || ""}
        onChange={(event) => set({ patientDescription: event.target.value })}
        helperText="Plain language, safe to show a patient"
      />

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <TextField
          label="Default Booster Interval (days)"
          type="number"
          fullWidth
          size="small"
          value={form.defaultBoosterIntervalDays}
          disabled={form.confersLifetimeImmunity}
          onChange={(event) =>
            set({ defaultBoosterIntervalDays: event.target.value })
          }
        />
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.confersLifetimeImmunity)}
              onChange={(event) => {
                const checked = event.target.checked;
                set({
                  confersLifetimeImmunity: checked,
                  ...(checked ? { defaultBoosterIntervalDays: "" } : {}),
                });
              }}
            />
          }
          label="Confers lifetime immunity"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.isNotifiable)}
              onChange={(event) => set({ isNotifiable: event.target.checked })}
            />
          }
          label="Notifiable disease"
        />
      </Box>

      <ExternalCodesField
        value={form.externalCodes}
        onChange={(next) => set({ externalCodes: next })}
        disabled={saving}
      />
    </MasterModalShell>
  );
};

export default AntigenModal;
